import { useCallback, useEffect, useRef, useState } from 'react'
import type { LoadMyEntitlements } from '@/modules/auth/use-cases'
import { reportError } from '@/shared/infra/observability'
import { useSubmitGuard } from '@/shared/ui/hooks'

export const PRO_REQUIRED_CREW_MESSAGE = 'BlindBook Pro is required to create a crew.'
export const ENTITLEMENTS_FAILED_MESSAGE =
  "We couldn't check your BlindBook Pro status. Please try again."

const PAYWALL_CLOSED_EVENT = 'proPaywallClosed'
const OPEN_PAYWALL_HANDLER = 'openProPaywall'

export type UseCrewBranchProps = {
  active: boolean
  onLoadMyEntitlements: LoadMyEntitlements
  onAdvance: () => void
}

export type UseCrewBranchResult = {
  isContinuing: boolean
  message: string
  handleContinue: () => Promise<void>
  showProRequired: () => void
}

export function useCrewBranch({
  active,
  onLoadMyEntitlements,
  onAdvance,
}: UseCrewBranchProps): UseCrewBranchResult {
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const [message, setMessage] = useState('')
  const [awaitingPaywall, setAwaitingPaywall] = useState(false)
  const recheckingRef = useRef(false)
  const loadRef = useRef(onLoadMyEntitlements)
  const advanceRef = useRef(onAdvance)
  loadRef.current = onLoadMyEntitlements
  advanceRef.current = onAdvance

  const evaluate = useCallback(async (): Promise<boolean | null> => {
    try {
      const entitlements = await loadRef.current()
      return entitlements.soloPro === true
    } catch (err) {
      reportError(err, { fallbackMessage: ENTITLEMENTS_FAILED_MESSAGE })
      setMessage(ENTITLEMENTS_FAILED_MESSAGE)
      return null
    }
  }, [])

  const handleContinue = useCallback(async () => {
    await runGuarded(async () => {
      setMessage('')
      const hasPro = await evaluate()
      if (hasPro === null) return
      if (hasPro) {
        advanceRef.current()
        return
      }
      setAwaitingPaywall(true)
      window.flutter_inappwebview
        ?.callHandler?.(OPEN_PAYWALL_HANDLER)
        ?.catch(() => {
          setAwaitingPaywall(false)
          setMessage(PRO_REQUIRED_CREW_MESSAGE)
        })
    })
  }, [runGuarded, evaluate])

  const showProRequired = useCallback(() => {
    setMessage(PRO_REQUIRED_CREW_MESSAGE)
  }, [])

  useEffect(() => {
    if (!active || !awaitingPaywall) return
    const handleClosed = () => {
      if (recheckingRef.current) return
      recheckingRef.current = true
      void evaluate()
        .then((hasPro) => {
          if (hasPro) {
            setMessage('')
            advanceRef.current()
          } else if (hasPro === false) {
            setMessage(PRO_REQUIRED_CREW_MESSAGE)
          }
        })
        .finally(() => {
          recheckingRef.current = false
        })
    }
    window.addEventListener(PAYWALL_CLOSED_EVENT, handleClosed)
    return () => window.removeEventListener(PAYWALL_CLOSED_EVENT, handleClosed)
  }, [active, awaitingPaywall, evaluate])

  useEffect(() => {
    if (!active) {
      setAwaitingPaywall(false)
      setMessage('')
    }
  }, [active])

  return { isContinuing: isSubmitting, message, handleContinue, showProRequired }
}
